import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';

function ChallengePlans() {
  const navigate = useNavigate();

  const plans = [
    {
      size: '$25,000',
      fee: '$79',
      title: 'Pro Challenge',
      icon: <RocketLaunchIcon sx={{ fontSize: 60, color: '#3b82f6' }} />,
      overview: 'The Pro Challenge is ideal for traders who want to demonstrate stable risk management with moderate targets. It is the perfect entry point to test your strategy under professional parameters.',
      details: [
        'Account Size: $25,000 (Demo Environment)',
        'Profit Target (Phase 1): 10%',
        'Profit Target (Phase 2): 5%',
        'Maximum Overall Drawdown: 10%',
        'Maximum Daily Drawdown: 5%',
        'Minimum Trading Days: 3',
        'Duration per Phase: Unlimited',
        'Leverage: 1:100',
        'Trading Style: All manual strategies allowed (No EAs or copy trading)'
      ]
    },
    {
      size: '$50,000',
      fee: '$134',
      title: 'Elite Challenge',
      icon: <EmojiEventsIcon sx={{ fontSize: 60, color: '#fbbf24' }} />,
      overview: 'For experienced traders aiming to showcase advanced discipline and strategy execution. The Elite Challenge allows greater flexibility while maintaining balanced risk parameters.',
      details: [
        'Account Size: $50,000 (Demo Environment)',
        'Profit Target (Phase 1): 10%',
        'Profit Target (Phase 2): 5%',
        'Maximum Overall Drawdown: 10%',
        'Maximum Daily Drawdown: 5%',
        'Minimum Trading Days: 3',
        'Duration per Phase: Unlimited',
        'Leverage: 1:100',
        'Trading Style: All manual strategies allowed (No EAs, or copy trading)'
      ]
    },
    {
      size: '$100,000',
      fee: '$256',
      title: 'Master Challenge',
      icon: <MilitaryTechIcon sx={{ fontSize: 60, color: '#a855f7' }} />,
      overview: 'The Master Challenge is designed for seasoned traders ready to showcase consistent performance across larger evaluation parameters. It emphasizes patience, drawdown control, and sustainable profitability.',
      details: [
        'Account Size: $100,000 (Demo Environment)',
        'Profit Target (Phase 1): 10%',
        'Profit Target (Phase 2): 5%',
        'Maximum Overall Drawdown: 10%',
        'Maximum Daily Drawdown: 5%',
        'Minimum Trading Days: 3',
        'Duration per Phase: Unlimited',
        'Leverage: 1:100',
        'Trading Style: Manual trading only (No automation or copy trading)'
      ]
    }
  ];

  return (
    <Box sx={{ bgcolor: '#131722', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 700, color: '#fff', mb: 3 }}>
            Challenge Plans
          </Typography>
          <Typography variant="h5" sx={{ color: '#d1d4dc', mb: 2 }}>
            Choose Your Evaluation Program
          </Typography>
          <Typography variant="body1" sx={{ color: '#d1d4dc', maxWidth: 900, mx: 'auto' }}>
            Each plan is designed to assess trading discipline, consistency, and risk management through structured performance objectives. Select the program that best fits your trading style and demonstrate your capability in a professional evaluation environment. We offer two platforms: MT5 and XFunding. You can choose either one to proceed with your Challenge.
          </Typography>
        </Box>

        {/* Plans Grid */}
        <Box sx={{ display: 'flex', gap: 4, mb: 6, flexWrap: 'nowrap', overflowX: 'auto', pt: 4 }}>
          {plans.map((plan, index) => (
            <Box key={index} sx={{ flex: '1 1 0', minWidth: 350 }}>
              <Card sx={{
                bgcolor: '#1e222d',
                border: '2px solid #2a2e39',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                transition: 'transform 0.2s, border-color 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  borderColor: '#3b82f6'
                }
              }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #162447 0%, #1f4068 100%)',
                    borderRadius: 2,
                    p: 3,
                    mb: 3,
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    {/* Icon at top */}
                    <Box sx={{ 
                      position: 'absolute', 
                      top: -30, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      bgcolor: '#131722',
                      borderRadius: '50%',
                      p: 1.5,
                      border: '3px solid #2a2e39',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {plan.icon}
                    </Box>
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                        {plan.size}
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#d1d4dc' }}>
                        Access Fee: {plan.fee}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#d1d4dc', mt: 0.5 }}>
                        (One-Time Service Charge)
                      </Typography>
                    </Box>
                  </Box>

                  {/* Title */}
                  <Typography variant="h5" sx={{ color: '#3b82f6', fontWeight: 600, mb: 2, textAlign: 'center' }}>
                    {plan.title}
                  </Typography>

                  {/* Overview */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                      Overview
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#d1d4dc' }}>
                      {plan.overview}
                    </Typography>
                  </Box>

                  {/* Key Details */}
                  <Box sx={{ mb: 3, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                      Key Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {plan.details.map((detail, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <CheckCircleIcon sx={{ color: '#4ade80', fontSize: 18, mt: 0.3, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: '#d1d4dc', fontSize: 13 }}>
                            {detail}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/signin')}
                    sx={{
                      background: 'linear-gradient(135deg, #162447 0%, #1f4068 100%)',
                      color: '#fff',
                      py: 1.5,
                      fontSize: 14,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1f4068 0%, #162447 100%)'
                      }
                    }}
                  >
                    Start {plan.size} Challenge
                  </Button>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Note to Participants */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Card sx={{ bgcolor: '#1e222d', border: '1px solid #2a2e39', maxWidth: 900, mx: 'auto' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                Note to Participants
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
                All evaluation accounts operate within a demo environment for the purpose of skill assessment and educational evaluation. Access fees are non-refundable and cover platform access, data usage, and performance monitoring tools. No live trading or investment services are provided by X Funding.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}

export default ChallengePlans;
